"use client";
import React, { useCallback } from "react";
import { cn } from "utils";
import CheckBox from "../../CheckBox";
import { TermsAgreementProps } from "./types";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/LoginGuidePopup/LoginGuideContent/TermsAgreement";

import { agreement } from "@/src/const";
import { MessageType } from "@/src/lib/bridge/types";
import { useBridge } from "@/src/lib/bridge/useBridge";

const I18n = I18nUtil.createScoped(Translations);

/**
 * TermsAgreement 组件
 *
 * 展示条款同意复选框和链接文本
 * 适合左右布局中的右侧内容区域底部
 *
 * @example
 * ```tsx
 * <TermsAgreement
 *   showCheckbox={true}
 *   termsAccepted={false}
 *   onTermsChange={(accepted) => console.log('条款同意:', accepted)}
 * />
 * ```
 */
export const TermsAgreement: React.FC<TermsAgreementProps> = ({
  termsAccepted,
  onTermsChange,
  className,
}) => {
  const { send, isInApp } = useBridge();

  const openExternalUrl = useCallback(
    (url: string) => {
      if (isInApp) {
        send(MessageType.OPEN_URL_EXTERNAL, { url });
        return;
      }
      window.open(url, "_blank");
    },
    [isInApp, send],
  );

  const handleTermsClick = () => {
    openExternalUrl(agreement.termsLink);
  };

  const handlePrivacyClick = () => {
    openExternalUrl(agreement.privacyLink);
  };

  return (
    <div
      className={cn("flex gap-3 items-start justify-start w-full", className)}
    >
      <CheckBox
        data-openreplay-label="LoginGuideContent · TermsAgreement · CheckBox"
        checked={termsAccepted}
        onClick={onTermsChange}
        iconSize={12}
      />
      {/* 条款文本 */}
      <div className="flex-1">
        <p className="body_s text-content_tertiary">
          <span className="text-content_tertiary">{`${I18n.t("By registering, you accept our")} `}</span>
          <span
            data-openreplay-label={`LoginGuideContent · TermsAgreement · span | ${I18n.t("Terms of Use")}`}
            className="text-content_secondary underline decoration-solid underline-offset-3 cursor-pointer bg-transparent border-0 p-0 hover:text-content_primary transition-colors"
            onClick={handleTermsClick}
          >
            {I18n.t("Terms of Use")}
          </span>
          <span className="text-content_tertiary">{` ${I18n.t("and")} `}</span>
          <span
            data-openreplay-label={`LoginGuideContent · TermsAgreement · span | ${I18n.t("Privacy Policy")}`}
            className="text-content_secondary underline decoration-solid underline-offset-3 cursor-pointer bg-transparent border-0 p-0 hover:text-content_primary transition-colors"
            onClick={handlePrivacyClick}
          >
            {I18n.t("Privacy Policy")}
          </span>
        </p>
      </div>
    </div>
  );
};

TermsAgreement.displayName = "TermsAgreement";
